import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { collection, query, where, getDocs, getDoc, deleteDoc, doc, updateDoc } from "firebase/firestore";
import { db, auth } from "../../../firebaseConfig";
import ConfirmModal from "../modals/confirmModal";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import "../ChatForm/SearchBar.css";
import { v4 as uuidv4 } from 'uuid';

import Doc0Icon from "../../assets/images/document-0.svg?url";
import Doc1Icon from "../../assets/images/document-0.svg?url";
import Doc2Icon from "../../assets/images/document-0.svg?url";

function DocSelector({ isOpen, onClose,  isNewDocOpen=true, handleNewDocToggle, closeModal, showActions=true, setCurrentDoc, onDataLoad="", setAvailableDocsList }) {
  const [docs, setDocs] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);
  const [docToEdit, setDocToEdit] = useState(null); // State for doc being edited
  const [newDocName, setNewDocName] = useState(""); // State for new doc name
  const [newDocDescription, setNewDocDescription] = useState(""); // State for new doc description
  const [newDocContent, setNewDocContent] = useState(""); // State for new doc content/instructions
  
  const docIcons = [Doc0Icon, Doc1Icon, Doc2Icon];

  useEffect(() => {
    const fetchDocs = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.error("No authenticated user found.");
        return;
      }
      const userUID = currentUser.uid;
      // Query the org's document directly
      const orgDocRef = doc(db, "users", userUID);
      try {
        const orgDocSnapshot = await getDoc(orgDocRef);
        // console.log("Fetching docs for org:", orgDocSnapshot);
        if (orgDocSnapshot.exists()) {
          const orgData = orgDocSnapshot.data();
          const docsData = orgData.docs || []; // Get the docs array or an empty array if it doesn't exist
          // const currentDocId = orgData.docCurrent; // No longer needed here for initial set

          setDocs(docsData);
          if (setAvailableDocsList) {
            setAvailableDocsList(docsData); // Pass the fetched docs to the parent
          }
          // GeminiComponent is now responsible for setting its initial agent after receiving this list.
        } else {
          console.log("No org document found.");
          setDocs([]);
        }
      } catch (error) {
        console.error("Error fetching docs:", error);
      }
    };

    fetchDocs();
  // This useEffect should only depend on setAvailableDocsList.
  // setCurrentDoc (the prop) should not be a dependency for the initial fetch logic.
  }, [setAvailableDocsList]);
  
  // Effect to reset form when it's closed, ensuring a clean state for creation or next edit
  useEffect(() => {
    if (!isNewDocOpen) {
      setDocToEdit(null);
      setNewDocName("");
      setNewDocDescription("");
      setNewDocContent("");
    }
  }, [isNewDocOpen]);

  const handleDelete = async () => {
    if (docToDelete) {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.error("No authenticated user found.");
        return;
      }
      const userUID = currentUser.uid;
      const orgDocRef = doc(db, "users", userUID); // Reference to the org's document

      try {
        const orgDocSnapshot = await getDoc(orgDocRef); // Get the org's document
        if (orgDocSnapshot.exists()) {
          const orgData = orgDocSnapshot.data();
          const updatedDocs = orgData.docs.filter((doc) => doc.id !== docToDelete); // Filter out the doc to delete
          await updateDoc(orgDocRef, { docs: updatedDocs }); // Update the org's document with the new array
          setDocs(updatedDocs); // Update the state
          console.log("Doc deleted successfully!");
        }
      } catch (error) {
        console.error("Error deleting doc:", error);
      }
      setIsModalOpen(false);
    }
  };

  const handleDocItemClick = async (selectedDoc) => {
    if (selectedDoc) {
      // console.log("Doc selected:" + selectedDoc.name + " : "+ selectedDoc.inst);
      if (setCurrentDoc) { // This is the prop from GeminiComponent
        setCurrentDoc(selectedDoc); // Inform GeminiComponent of the org's selection
      }

      

      // Also save the selected document ID to Firebase
      const currentUser = auth.currentUser;
      if (currentUser) {
        const userUID = currentUser.uid;
        const orgDocRef = doc(db, "users", userUID);
        try {
          await updateDoc(orgDocRef, { docCurrent: selectedDoc.id });
          console.log("Saved current doc ID to org document:", selectedDoc.id);
        } catch (error) {
          console.error("Error saving current doc ID:", error);
        }
      }
      if (closeModal) {
        closeModal(); // Call the passed function to close the modal in GeminiComponent
      }
      if (showActions === false) {
        // onDataLoad(selectedDoc); // Pass the data up
      }
    }
  };

  const handleEditClick = (e, doc) => {
    e.stopPropagation(); // Prevent the agent selection click from firing
    setDocToEdit(doc.id);
    setNewDocName(doc.name);
    setNewDocDescription(doc.desc);
    setNewDocContent(doc.content);
    handleNewDocToggle(true); // Open the form for editing
  };

  const handleSaveDocClick = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.error("No authenticated org found.");
      return;
    }
    const userUID = currentUser.uid;
    const orgDocRef = doc(db, "users", userUID);

    try {
      const orgDocSnapshot = await getDoc(orgDocRef);
      if (orgDocSnapshot.exists()) {
        const orgData = orgDocSnapshot.data();
        let updatedDocs;

        if (docToEdit) {
          // Editing an existing doc
          console.log("Updating doc...");
          updatedDocs = (orgData.docs || []).map(doc =>
            doc.id === docToEdit
              ? { ...doc, name: newDocName, desc: newDocDescription, content: newDocContent }
              : doc
          );
        } else {
          // Creating a new doc
          console.log("Saving new doc...");
          const newDoc = {
            id: uuidv4(), // Generate a unique ID
            name: newDocName,
            desc: newDocDescription,
            content: newDocContent,
          };
          updatedDocs = [...(orgData.docs || []), newDoc];
        }

        await updateDoc(orgDocRef, { docs: updatedDocs });
        setDocs(updatedDocs); // Update the state
        if (setAvailableDocsList) {
          setAvailableDocsList(updatedDocs); // Update parent component's list
        }

        // If the edited doc is the current doc, update it in the parent
        if (docToEdit && setCurrentDoc) {
            const updatedDoc = updatedDocs.find(d => d.id === docToEdit);
            setCurrentDoc(updatedDoc);
        }

        handleNewDocToggle(false); // Close the modal
        console.log(`Doc ${docToEdit ? 'updated' : 'saved'} successfully!`);
      }
    } catch (error) {
      console.error(`Error ${docToEdit ? 'updating' : 'saving'} doc:`, error);
    }
  };

  return (

    <div >
     {isNewDocOpen === true && (
      
        <div className="drop-list" style={{ width: "480px", padding: "12px"}} >
          <p className="overline">{docToEdit ? "Edit Doc Name" : "Give your doc a name"}</p>
          <input
            className="input"
            type="text"
            placeholder="Q3 Earnings, Competitor Analysis, etc."
            value={newDocName}
            onChange={(e) => setNewDocName(e.target.value)}
          />

          <p className="overline">{docToEdit ? "Edit Description" : "Add a description"}</p>
          <input
            className="input"
            type="multiline"
            placeholder="A collection of documents for..."
            value={newDocDescription}
            onChange={(e) => setNewDocDescription(e.target.value)}
          />

          <p className="overline">{docToEdit ? "Edit Content" : "Add content or a summary"}</p>
          <textarea
            className="input"
            type="multiline"
            placeholder="This document contains..."
            value={newDocContent}
            onChange={(e) => setNewDocContent(e.target.value)}
            style={{ height: '120px', resize: 'vertical' }} // Add inline style for height and allow vertical resize
            rows={8}
          />
        <button className="button" type="button" onClick={handleSaveDocClick}>
          {docToEdit ? "Update Doc" : "Save this Doc"}
        </button>
        </div>
      )}

       {isOpen === true && isNewDocOpen === false && (
        

        <div className="drop-list"  style={{ width: "480px", padding: "12px" }}>

        <ul style={{ listStyleType: "none", margin: "0", padding: "0" }}>
        {docs && docs.length > 0 ? (
          docs.map((doc, index) => (
            <li className="recentsList"  style={{ padding: "0"}} key={doc.id}>
              <button style={{ padding: "0 0 16px 16px"}} className="pageList" onClick={() => handleDocItemClick(doc)}>

                <div style={{ color: "var(--Gray7", marginLeft: "4px", flexDirection: "row", display: "flex", alignItems: "middle", justifyContent: "center" }}>
                {/* Doc icon on the left side */}

                 <div style={{ marginRight: "12px", maxWidth: "64px",  height: "72px",  flexDirection: "column", display: "flex", padding: "4px", borderRadius: "0 0 8px 8px", backgroundColor: "#84A3AA", display: "flex", alignItems: "center", justifyContent: "center" }}>
                {/* Use index % 3 to cycle through docIcons */}
                <img src={docIcons[index % docIcons.length]} alt={`Doc ${index}`} style={{ width: "52px", height: "52px", borderRadius: "0px" }} />
              </div>
              {/* Doc name and description on the right side */}
              <div style={{ width: "96%"}}>
                <h3 style={{ color: "var(--Gray7", marginBottom: "-6px", width: "96%"}}>{doc.name || doc.title || doc.id}</h3>
                <br></br>
                <p>{doc.desc}</p>
                </div>
                </div>
              <div className="library-item-actions" style={{ width: "72px"}}>
                {showActions && (
                  <button
                    onClick={(e) => handleEditClick(e, doc)}
                    className="delBtn"
                  >
                    <EditIcon />
                  </button>
                )}
                {showActions && (
                <button
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent the doc selection click from firing
                    setDocToDelete(doc.id);
                    setIsModalOpen(true);
                  }}
                  className="delBtn"
                >
                  <DeleteIcon />
                </button>
              )}
              </div>
              </button>
            </li>
          ))
        ) : (
          <li>Loading docs...</li>
        )}
      </ul>

       <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: "10px" }}>
            <button
              className="resFooterButton"
              onClick={() => handleNewDocToggle(true)}
              style={{ margin: "0px", width: "48px", height: "48px", display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 13H13V19H11V13H5V11H11V5H13V11H19V13Z" />
              </svg>
            </button>
          </div>

      </div>
      
          )}


      <ConfirmModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleDelete}
      />

    </div>
  );
}

export default DocSelector;
